/**
 * ChannelsApi — Channel intelligence and domain & channel comparison.
 *
 * Extracted from client.ts (CUR-547 god-object split). Methods-only by
 * design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type { AnalysisProgressResponse, ChannelCardListResponse, ChannelComparisonRequest, ChannelComparisonResponse, ChannelEligibilityResponse, DomainDetailResponse, DomainStatsListResponse, ExtendPreviewResponse, ExtendProgressResponse, ExtendTriggerRequest, ExtendTriggerResponse, GenerateInsightsRequest, GenerateInsightsResponse, ReportDetailResponse, ReportSummaryResponse, RollupCandidateResponse, RollupDetailResponse, RollupProgressResponse, RollupSummaryResponse, TriggerAnalysisRequest, TriggerAnalysisResponse, TriggerRollupRequest, TriggerRollupResponse } from '../../../../shared/types/api';
import { APIError as SharedAPIError, buildApiUrl, createAuthHeaders, processApiResponse } from '../../../../shared/utils/api-helpers';

import { BaseApiClient } from './base';

export interface CompareCandidate {
  publisher_id: number;
  name: string;
  platform: string;
}

export class ChannelsApi extends BaseApiClient {
  // ============================================================================
  // Channel Intelligence Methods
  // ============================================================================

  /**
   * Check if a channel is eligible for intelligence analysis
   */
  async checkChannelEligibility(publisherId: number): Promise<ChannelEligibilityResponse> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/eligibility/${publisherId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<ChannelEligibilityResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Batch check eligibility for multiple channels in a single request
   */
  async batchCheckChannelEligibility(publisherIds: number[]): Promise<Record<number, ChannelEligibilityResponse>> {
    const url = buildApiUrl(this.baseUrl, '/admin/channel-intelligence/eligibility/batch');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ publisher_ids: publisherIds }),
    });
    const result = await processApiResponse<{ results: Record<number, ChannelEligibilityResponse> }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!.results;
  }

  /**
   * Trigger channel intelligence analysis for a publisher
   */
  async triggerChannelAnalysis(request: TriggerAnalysisRequest): Promise<TriggerAnalysisResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/channel-intelligence/analyze');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const result = await processApiResponse<TriggerAnalysisResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Resume a failed channel analysis from its last successful checkpoint.
   *
   * Backend: POST /admin/channel-intelligence/resume (CUR-537 fix B).
   * Reads the persisted ``agent_results.full_output`` for each completed stage
   * on the failed task and queues a fresh task that the orchestrator skip-resumes
   * into.
   */
  async resumeChannelAnalysis(
    taskId: string,
    maxVideos?: number
  ): Promise<TriggerAnalysisResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/channel-intelligence/resume');
    const headers = createAuthHeaders(await this.resolveToken());

    // No default: an unset maxVideos flows through as undefined, and
    // JSON.stringify drops undefined-valued keys, so the request body omits
    // max_videos entirely rather than pinning it to 50 — the backend then
    // applies its own per-lane defaults.
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ task_id: taskId, max_videos: maxVideos }),
    });

    const result = await processApiResponse<TriggerAnalysisResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get progress of an ongoing channel analysis by task ID
   */
  async getAnalysisProgress(taskId: string): Promise<AnalysisProgressResponse> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/progress/${taskId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<AnalysisProgressResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get active analysis progress for a publisher
   */
  async getPublisherAnalysisProgress(publisherId: number): Promise<AnalysisProgressResponse | null> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/progress/publisher/${publisherId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });

    if (response.status === 204 || response.status === 404) {
      return null;
    }

    const result = await processApiResponse<AnalysisProgressResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data ?? null;
  }

  /**
   * Get full details of a channel intelligence report
   */
  async getChannelReport(reportId: string): Promise<ReportDetailResponse> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/report/${reportId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<ReportDetailResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get analysis history for a publisher
   */
  async getPublisherReports(publisherId: number, limit: number = 10): Promise<ReportSummaryResponse[]> {
    const url = buildApiUrl(
      this.baseUrl,
      `/admin/channel-intelligence/reports/publisher/${publisherId}?limit=${limit}`
    );
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<ReportSummaryResponse[]>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data ?? [];
  }

  /**
   * Get the most recent report for a publisher
   */
  async getLatestReport(publisherId: number): Promise<ReportDetailResponse | null> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/reports/latest/${publisherId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });

    if (response.status === 204 || response.status === 404) {
      return null;
    }

    const result = await processApiResponse<ReportDetailResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data ?? null;
  }

  /**
   * Channels with a report, for the Compare picker
   */
  async getCompareCandidates(search: string): Promise<CompareCandidate[]> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.fetchJSON<CompareCandidate[]>(`/admin/channel-intelligence/compare/candidates${query}`, { transformKeys: false });
  }

  /**
   * Get channel cards for all analyzed channels (dashboard overview)
   */
  async getAllChannelCards(limit: number = 50): Promise<ChannelCardListResponse> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/channel-cards?limit=${limit}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<ChannelCardListResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Delete a channel intelligence report
   */
  async deleteChannelReport(reportId: string): Promise<{ success: boolean; message: string }> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/report/${reportId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    const result = await processApiResponse<{ success: boolean; message: string }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Domain & Channel Comparison Methods
  // ============================================================================

  /**
   * Get all domains with aggregated statistics (channels, videos, hours)
   */
  async getDomainsWithStats(): Promise<DomainStatsListResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/domains');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<DomainStatsListResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get domain detail with channels list
   */
  async getDomainDetail(domainId: number): Promise<DomainDetailResponse> {
    const url = buildApiUrl(this.baseUrl, `/admin/domains/${domainId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<DomainDetailResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Compare multiple channels side-by-side
   */
  async compareChannels(request: ChannelComparisonRequest): Promise<ChannelComparisonResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/channels/compare');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const result = await processApiResponse<ChannelComparisonResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Generate AI comparison insights for channels
   */
  async generateComparisonInsights(request: GenerateInsightsRequest): Promise<GenerateInsightsResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/channels/compare/generate');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const result = await processApiResponse<GenerateInsightsResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /** List channels eligible for a roll-up (completed latest report). Super-admin. SP4 PR-B. */
  async getRollupCandidates(): Promise<RollupCandidateResponse[]> {
    const url = buildApiUrl(this.baseUrl, '/admin/channel-intelligence/rollups/candidates');
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<RollupCandidateResponse[]>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  /** Queue synthesis of a manually-selected roll-up. Super-admin. SP4 PR-B. */
  async triggerRollup(request: TriggerRollupRequest): Promise<TriggerRollupResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/channel-intelligence/rollups');
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    const result = await processApiResponse<TriggerRollupResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  /** Poll roll-up synthesis progress (raw Celery AsyncResult shape). Super-admin. SP4 PR-B. */
  async getRollupProgress(taskId: string): Promise<RollupProgressResponse> {
    const url = buildApiUrl(this.baseUrl, `/admin/channel-intelligence/rollups/progress/${taskId}`);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<RollupProgressResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  /** List existing roll-ups for an org. Super-admin. SP4 PR-B. */
  async listRollups(organizationId: string): Promise<RollupSummaryResponse[]> {
    const url = buildApiUrl(
      this.baseUrl,
      `/admin/channel-intelligence/rollups?organization_id=${encodeURIComponent(organizationId)}`,
    );
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<RollupSummaryResponse[]>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  /** Fetch a single roll-up's detail. Super-admin. SP4 PR-B. */
  async getRollupDetail(
    normalizedName: string,
    organizationId: string,
  ): Promise<RollupDetailResponse> {
    const url = buildApiUrl(
      this.baseUrl,
      `/admin/channel-intelligence/rollups/${encodeURIComponent(normalizedName)}?organization_id=${encodeURIComponent(organizationId)}`,
    );
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<RollupDetailResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  /** Free coverage/cost preview for the extend-analysis backfill. Super-admin. CUR-1246. */
  async getExtendPreview(publisherId: number, months: number): Promise<ExtendPreviewResponse> {
    const url = buildApiUrl(
      this.baseUrl,
      `/admin/channel-intelligence/extend/preview/${publisherId}?months=${months}`,
    );
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<ExtendPreviewResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  /** Trigger an extend-analysis backfill. Super-admin. CUR-1246. */
  async triggerExtend(request: ExtendTriggerRequest): Promise<ExtendTriggerResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/channel-intelligence/extend');
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    const result = await processApiResponse<ExtendTriggerResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  /** Poll extend-analysis progress (raw Celery AsyncResult shape). Super-admin. CUR-1246. */
  async getExtendProgress(taskId: string): Promise<ExtendProgressResponse> {
    const url = buildApiUrl(
      this.baseUrl,
      `/admin/channel-intelligence/extend/progress/${taskId}`,
    );
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<ExtendProgressResponse>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

}
